import { forwardRef, type ButtonHTMLAttributes } from 'react';
import { cva, type VariantProps } from 'class-variance-authority';
import type { LucideIcon } from 'lucide-react';
import { cn } from '@/lib/utils';

/**
 * IconButton — for icon-only buttons (close X, back chevron, inline actions).
 *
 * Four shapes cover the visual patterns found in the codebase:
 * - `round` — drawer/modal header buttons (close, back). Pill hover background.
 * - `square` — toolbar / inline panel actions. Rounded-rect hover background.
 * - `bare` — tiny inline indicators (clear, dismiss). No padding/background; color shifts on hover.
 * - `row-action` — list-row actions (view / edit / revoke / delete). Low-emphasis
 *   `ink-400` idle, colored hover via the `tone` prop. The colored hover signals
 *   the semantic of the action (warning / danger / info).
 *
 * Sizes use the canonical responsive pattern: `md` is `p-1.5 sm:p-2` so the same
 * primitive feels right on mobile and desktop.
 *
 * Pass an `aria-label` — the button has no visible text.
 */
export const iconButtonVariants = cva(
  'inline-flex items-center justify-center transition-colors ' +
    'focus:outline-none focus-visible:ring-2 focus-visible:ring-offset-1 focus-visible:ring-ink-500/40 ' +
    'disabled:pointer-events-none disabled:opacity-60',
  {
    variants: {
      shape: {
        round: 'rounded-full text-ink-700 hover:bg-ink-100',
        square: 'rounded-lg text-ink-700 hover:bg-ink-100',
        bare: 'text-ink-400 hover:text-ink-600',
        'row-action': 'rounded-lg text-ink-400',
      },
      // `tone` only takes effect when `shape="row-action"` — see compoundVariants.
      // Ignored (silently) for other shapes so callers don't have to clear it
      // when changing shape.
      tone: {
        default: '',
        accent: '',
        warning: '',
        danger: '',
        success: '',
      },
      size: {
        sm: 'p-1',
        md: 'p-1.5 sm:p-2',
        lg: 'p-2 sm:p-2.5',
      },
    },
    compoundVariants: [
      { shape: 'bare', size: 'sm', class: 'p-0' },
      { shape: 'bare', size: 'md', class: 'p-0' },
      { shape: 'bare', size: 'lg', class: 'p-0' },
      // Row-action hover colors per tone.
      { shape: 'row-action', tone: 'default', class: 'hover:text-ink-600 hover:bg-ink-100' },
      { shape: 'row-action', tone: 'accent',  class: 'hover:text-accent-600 hover:bg-accent-50' },
      { shape: 'row-action', tone: 'warning', class: 'hover:text-warning-600 hover:bg-warning-50' },
      { shape: 'row-action', tone: 'danger',  class: 'hover:text-danger-600 hover:bg-danger-50' },
      { shape: 'row-action', tone: 'success', class: 'hover:text-success-600 hover:bg-success-50' },
    ],
    defaultVariants: {
      shape: 'square',
      tone: 'default',
      size: 'md',
    },
  }
);

const ICON_SIZE_MAP = {
  sm: 'h-4 w-4',
  md: 'h-5 w-5',
  lg: 'h-6 w-6',
} as const;

export interface IconButtonProps
  extends ButtonHTMLAttributes<HTMLButtonElement>,
    VariantProps<typeof iconButtonVariants> {
  icon: LucideIcon;
  iconClassName?: string;
}

export const IconButton = forwardRef<HTMLButtonElement, IconButtonProps>(
  ({ className, shape, tone, size, icon: Icon, iconClassName, type = 'button', ...props }, ref) => {
    const resolvedIconSize = iconClassName ?? ICON_SIZE_MAP[size ?? 'md'];
    return (
      <button
        ref={ref}
        type={type}
        className={cn(iconButtonVariants({ shape, tone, size }), className)}
        {...props}
      >
        <Icon className={resolvedIconSize} aria-hidden="true" />
      </button>
    );
  }
);
IconButton.displayName = 'IconButton';
